import { Link, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DocumentPdfButton from "@/components/base/DocumentPdfButton";
import PrintButton from "@/components/base/PrintButton";
import EmptyState from "@/components/base/EmptyState";
import WhatsappButton from "@/components/base/WhatsappButton";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_TONES,
  AGENCE_TYPE_PIECE_LABELS,
  agenceContratReference,
  agenceLocataireInitiales,
  agenceLocataireNomComplet,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { formatDate, formatFcfa } from "@/utils/format";
import { exportSummaryPdf, printSummaryPdf, type PdfDocumentOptions, type PdfSection } from "@/utils/pdfDocument";

export default function AgenceLocataireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getLocataire, contratsByLocataire, getBien, getImmeuble } = useAgenceData();
  const { getProprietaire } = useProprietaires();
  const locataire = id ? getLocataire(id) : undefined;

  if (!locataire) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/locataires"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux locataires
        </Link>
        <EmptyState
          icon="ri-user-3-line"
          title="Locataire introuvable"
          description="Ce locataire n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/agence/locataires"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les locataires
            </Link>
          }
        />
      </div>
    );
  }

  const nom = agenceLocataireNomComplet(locataire);
  const contrats = contratsByLocataire(locataire.id);
  const biensOccupes = contrats
    .filter((contrat) => contrat.statut === "actif")
    .map((contrat) => getBien(contrat.bienId))
    .filter((bien): bien is NonNullable<typeof bien> => Boolean(bien));

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Prénom", value: locataire.prenom || "—", icon: "ri-user-line" },
    { label: "Nom", value: locataire.nom || "—", icon: "ri-user-line" },
    { label: "Téléphone", value: locataire.telephone || "—", icon: "ri-phone-line" },
    { label: "WhatsApp", value: locataire.whatsapp || "Non renseigné", icon: "ri-whatsapp-line" },
    { label: "Email", value: locataire.email || "—", icon: "ri-mail-line" },
    { label: "Profession", value: locataire.profession || "—", icon: "ri-briefcase-line" },
    { label: "Adresse", value: locataire.adresse || "—", icon: "ri-map-pin-line" },
    {
      label: "Type de pièce d'identité",
      value: locataire.typePiece ? AGENCE_TYPE_PIECE_LABELS[locataire.typePiece] : "—",
      icon: "ri-id-card-line",
    },
    { label: "Numéro de pièce", value: locataire.numeroPiece || "—", icon: "ri-hashtag" },
    { label: "Contact d'urgence", value: locataire.contactUrgence || "—", icon: "ri-phone-find-line" },
  ];

  const buildPdfOptions = (): PdfDocumentOptions => {
    const sections: PdfSection[] = [
      {
        heading: "Informations du locataire",
        fields: infos.map((info) => ({ label: info.label, value: info.value })),
      },
    ];
    if (locataire.notes) {
      sections.push({ heading: "Notes", note: locataire.notes });
    }
    if (contrats.length > 0) {
      sections.push({
        heading: "Contrats du locataire",
        table: {
          columns: ["Référence", "Bien", "Immeuble", "Loyer / mois", "Statut"],
          rows: contrats.map((contrat) => {
            const bien = getBien(contrat.bienId);
            const immeuble = getImmeuble(contrat.immeubleId);
            return [
              agenceContratReference(contrat.id),
              bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "—",
              immeuble ? immeuble.nom : "—",
              formatFcfa(contrat.loyerMensuel),
              AGENCE_CONTRAT_STATUT_LABELS[contrat.statut],
            ];
          }),
        },
      });
    }
    if (biensOccupes.length > 0) {
      sections.push({
        heading: "Biens occupés",
        table: {
          columns: ["Bien", "Immeuble"],
          rows: biensOccupes.map((bien) => [
            bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type],
            getImmeuble(bien.immeubleId)?.nom ?? "—",
          ]),
        },
      });
    }
    return {
      filename: `locataire-${nom}`,
      title: nom,
      subtitle: [locataire.profession, locataire.telephone].filter(Boolean).join(" · ") || "Locataire",
      sections,
    };
  };

  const handleExportPdf = () => exportSummaryPdf(buildPdfOptions());
  const handlePrint = () => printSummaryPdf(buildPdfOptions());

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/locataires"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux locataires
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-lg font-semibold text-primary-700">
            {agenceLocataireInitiales(locataire)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary" icon="ri-user-3-line">
                Locataire
              </Badge>
              {locataire.typePiece && (
                <Badge tone="neutral" icon="ri-id-card-line">
                  {AGENCE_TYPE_PIECE_LABELS[locataire.typePiece]}
                </Badge>
              )}
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{nom}</h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-phone-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">{locataire.telephone || "Téléphone non renseigné"}</span>
            </p>
            <p className="mt-1 text-xs text-foreground-400">Ajouté le {formatDate(locataire.createdAt)}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <Link
            to={`/agence/contrats?locataire=${locataire.id}&nouveau=1`}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-file-add-line text-base" aria-hidden="true" />
            Créer un contrat
          </Link>
          <Link
            to="/agence/locataires"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-list-check" aria-hidden="true" />
            Tous les locataires
          </Link>
          <WhatsappButton numero={locataire.whatsapp} />
          <DocumentPdfButton onExport={handleExportPdf} />
          <PrintButton onPrint={handlePrint} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                <i className="ri-information-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                Informations du locataire
              </h3>
            </header>
            <div className="p-4 md:p-5">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {infos.map((info) => (
                  <div key={info.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                      <i className={`${info.icon} text-base`} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-xs text-foreground-500">{info.label}</dt>
                      <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>

              {locataire.notes && (
                <div className="mt-5 border-t border-background-200 pt-4">
                  <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">Notes</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{locataire.notes}</p>
                </div>
              )}
            </div>
          </section>

          <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
              <span className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-file-text-line text-base" aria-hidden="true" />
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                  Contrats du locataire
                </h3>
              </span>
              <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
                {contrats.length}
              </span>
            </header>
            <div className="p-4 md:p-5">
              {contrats.length === 0 ? (
                <p className="text-sm text-foreground-500">
                  Aucun contrat pour ce locataire. Créez un contrat depuis le module Contrats pour le rattacher à un bien.
                </p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {contrats.map((contrat) => {
                    const bien = getBien(contrat.bienId);
                    const immeuble = getImmeuble(contrat.immeubleId);
                    return (
                      <li key={contrat.id}>
                        <Link
                          to={`/agence/contrats/${contrat.id}`}
                          className="flex flex-col gap-2 rounded-md border border-background-200 p-3.5 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between cursor-pointer"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-label font-medium text-foreground-950">
                              {agenceContratReference(contrat.id)} ·{" "}
                              {bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "Bien"}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-foreground-500">
                              {immeuble ? immeuble.nom : "Immeuble non renseigné"} ·{" "}
                              {formatFcfa(contrat.loyerMensuel)} / mois
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>
                              {AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}
                            </Badge>
                            <i className="ri-arrow-right-s-line text-base text-foreground-400" aria-hidden="true" />
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
              <span className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
                  <i className="ri-home-smile-2-line text-base" aria-hidden="true" />
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Biens occupés</h3>
              </span>
              <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
                {biensOccupes.length}
              </span>
            </header>
            <div className="p-4 md:p-5">
              {biensOccupes.length === 0 ? (
                <p className="text-sm text-foreground-500">
                  Aucun bien occupé actuellement. Les biens apparaissent ici dès qu'un contrat actif les relie à ce
                  locataire.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {biensOccupes.map((bien) => {
                    const immeuble = getImmeuble(bien.immeubleId);
                    const proprietaire = getProprietaire(bien.proprietaireId);
                    return (
                      <Link
                        key={bien.id}
                        to={`/agence/biens/${bien.id}`}
                        className="flex items-center gap-3 rounded-md border border-background-200 p-3 transition-colors hover:bg-background-100 cursor-pointer"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                          <i className="ri-home-4-line text-base" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-label font-medium text-foreground-950">
                            {bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type]}
                          </span>
                          <span className="block truncate text-xs text-foreground-500">
                            {immeuble ? immeuble.nom : "Immeuble non renseigné"}
                            {proprietaire ? ` · ${proprietaireNomComplet(proprietaire)}` : ""}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="flex h-fit flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {[
              { label: "Contrats", to: `/agence/contrats?locataire=${locataire.id}`, icon: "ri-file-text-line", hint: "Baux et échéances" },
              { label: "Biens", to: "/agence/biens", icon: "ri-home-4-line", hint: "Logements occupés" },
              { label: "Immeubles", to: "/agence/immeubles", icon: "ri-building-2-line", hint: "Bâtiments concernés" },
              { label: "Loyers", to: `/agence/loyers?locataire=${locataire.id}`, icon: "ri-wallet-3-line", hint: "Suivi par période" },
              { label: "Paiements", to: `/agence/paiements?locataire=${locataire.id}`, icon: "ri-hand-coin-line", hint: "Encaissements" },
              { label: "Documents", to: `/agence/documents?locataire=${locataire.id}`, icon: "ri-folder-line", hint: "Pièces et justificatifs" },
            ].map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}